import { useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, ReactNode } from 'react';

interface PageTransitionProps {
  children: ReactNode;
}

/**
 * Wraps page content and plays a fade + subtle upward slide on every route change.
 *
 * Phases:
 *  'idle'  → page fully visible, no transition active
 *  'out'   → current page fading out (160ms)
 *  'swap'  → content swapped, invisible, about to fade in
 *  'in'    → new page fading in (300ms)
 */
export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();

  type Phase = 'idle' | 'out' | 'swap' | 'in';
  const [phase, setPhase] = useState<Phase>('idle');
  const [displayChildren, setDisplayChildren] = useState(children);
  const prevPathname = useRef(location.pathname);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => {
    // Same path — just update children in place (e.g. search param change)
    if (location.pathname === prevPathname.current) {
      setDisplayChildren(children);
      return;
    }

    prevPathname.current = location.pathname;
    clearTimers();

    // ① fade out current page
    setPhase('out');

    // ② swap content while still invisible
    const t1 = setTimeout(() => {
      setDisplayChildren(children);
      setPhase('swap');
    }, 150);

    // ③ one RAF to let browser paint the invisible state, then fade in
    const t2 = setTimeout(() => {
      setPhase('in');
    }, 160); // 150 + ~10ms for the RAF

    // ④ settle to idle
    const t3 = setTimeout(() => {
      setPhase('idle');
    }, 160 + 320);

    timers.current = [t1, t2, t3];
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ─── per-phase styles ─── */
  const style = (): React.CSSProperties => {
    switch (phase) {
      case 'out':
        return {
          opacity: 0,
          transform: 'translateY(-5px)',
          transition: 'opacity 150ms ease, transform 150ms ease',
          willChange: 'opacity, transform',
          pointerEvents: 'none',
        };
      case 'swap':
        // Invisible, no transition (instant snap)
        return {
          opacity: 0,
          transform: 'translateY(6px)',
          transition: 'none',
          willChange: 'opacity, transform',
          pointerEvents: 'none',
        };
      case 'in':
        return {
          opacity: 1,
          transform: 'translateY(0)',
          transition: 'opacity 320ms ease, transform 320ms ease',
          willChange: 'opacity, transform',
        };
      default: // 'idle'
        return {
          opacity: 1,
          transform: 'none',
          willChange: 'auto',
        };
    }
  };

  return <div style={style()}>{displayChildren}</div>;
}